import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLayout } from '../../components/DesignSystem';
import { Button, Card, Input, Modal, Select, Space, Table, message } from 'antd';
import { SearchOutlined, InboxOutlined, UndoOutlined, UploadOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { ListPagination, paginateList } from '../../components/ListPagination';
import './ProductList.css';

// ── CSV column aliases ──────────────────────────────────────────────────────
// Maps our internal field names to all the column headers we accept,
// including Shopify, Zoho Inventory, and generic formats.
const CSV_ALIASES: Record<string, string[]> = {
  name:            ['name','product name','title','item name'],
  sku:             ['sku','variant sku','item code','product code'],
  price:           ['price','variant price','selling price','rate','retail price','sale price'],
  cost:            ['cost','cost price','purchase rate','purchase price','variant compare at price'],
  stock:           ['stock','quantity','inventory','variant inventory qty','opening stock','stock on hand','qty'],
  category:        ['category','product category','category name','product type','type'],
  description:     ['description','body (html)','store description','product description','body','long description'],
  unit:            ['unit','unit of measure','uom'],
  barcode:         ['barcode','variant barcode','upc','ean','isbn'],
  supplier:        ['supplier','vendor','brand','manufacturer'],
  weight:          ['weight','variant grams','package weight'],
  min_stock:       ['min stock','reorder level','reorder point','min_stock','minimum stock'],
  wholesale_price: ['wholesale price','wholesale_price','trade price','b2b price'],
  box_quantity:    ['box quantity','units per box','box_quantity','pack size','box size'],
  min_order_qty:   ['min order qty','minimum order qty','min_order_qty','moq','minimum order quantity'],
  status:          ['status','published'],
  notes:           ['notes','tags','internal notes','memo'],
  tax_rate:        ['tax rate','tax_rate','taxrate','vat rate','tax %','vat %'],
  expiry_date:     ['expiry date','expiry_date','best before','use by','use-by','bb date'],
};

function normalise(s: string) { return s.toLowerCase().replace(/[^a-z0-9 ()%]/g, '').trim(); }

type ExpiryLevel = 'none' | 'ok' | 'soon' | 'expired';

function getExpiryStatus(expiryDate?: string | null): { label: string; level: ExpiryLevel } {
  if (!expiryDate) return { label: '—', level: 'none' };
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const exp = new Date(expiryDate);
  if (Number.isNaN(exp.getTime())) return { label: '—', level: 'none' };
  exp.setHours(0, 0, 0, 0);
  const days = Math.ceil((exp.getTime() - today.getTime()) / 86400000);
  if (days < 0) return { label: `Expired ${Math.abs(days)}d ago`, level: 'expired' };
  if (days === 0) return { label: 'Expires today', level: 'expired' };
  if (days <= 30) {
    return {
      label: days === 1 ? '1 day left' : `${days} days left`,
      level: 'soon',
    };
  }
  return {
    label: exp.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    level: 'ok',
  };
}

function isExpiringSoon(expiryDate?: string | null): boolean {
  const { level } = getExpiryStatus(expiryDate);
  return level === 'soon' || level === 'expired';
}

function parseCSV(text: string): Record<string, string>[] {
  const lines = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n').split('\n').filter(l => l.trim());
  if (lines.length < 2) return [];

  // Split a CSV line respecting double-quoted fields
  const splitLine = (line: string): string[] => {
    const cols: string[] = [];
    let cur = '', inQ = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') { if (inQ && line[i+1] === '"') { cur += '"'; i++; } else inQ = !inQ; }
      else if (ch === ',' && !inQ) { cols.push(cur); cur = ''; }
      else cur += ch;
    }
    cols.push(cur);
    return cols.map(c => c.trim().replace(/^"|"$/g, ''));
  };

  const rawHeaders = splitLine(lines[0]);
  // Build header→our_field map
  const headerMap: Record<number, string> = {};
  rawHeaders.forEach((h, idx) => {
    const norm = normalise(h);
    for (const [field, aliases] of Object.entries(CSV_ALIASES)) {
      if (aliases.includes(norm)) { headerMap[idx] = field; break; }
    }
  });

  return lines.slice(1).map(line => {
    const vals = splitLine(line);
    const row: Record<string, string> = {};
    vals.forEach((v, idx) => { if (headerMap[idx]) row[headerMap[idx]] = v; });
    return row;
  }).filter(r => Object.keys(r).length > 0);
}

const ProductList: React.FC = () => {
  const [products, setProducts] = useState<any[]>([]);
  const [listPage, setListPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [stockFilter, setStockFilter] = useState('All');

  // Import CSV state
  const [importOpen, setImportOpen] = useState(false);
  const [importRows, setImportRows] = useState<Record<string, string>[]>([]);
  const [importFileName, setImportFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<{ inserted: number; failed: number; errors: any[] } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  useEffect(() => {
    const flash = (location.state as { flash?: string } | null)?.flash;
    if (flash) {
      message.success(flash);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchProducts();
  }, [token, navigate]);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm, categoryFilter, statusFilter, stockFilter]);

  const fetchProducts = async () => {
    try {
      setError(null);
      const url = `${API_URL}/products?include_archived=true`;
      const res = await apiFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setProducts(data);
      } else if (data && Array.isArray(data.data)) {
        setProducts(data.data);
      } else {
        setError(
          typeof data === 'object' && data !== null && data.error
            ? `API error: ${data.error}`
            : 'Unexpected API response: not an array.'
        );
        setProducts([]);
      }
    } catch (err) {
      console.error('Failed to fetch products:', err);
      setError('Failed to load products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFileName(file.name);
    setImportResult(null);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      const rows = parseCSV(text);
      if (rows.length === 0) {
        message.error('No valid rows found. Check the CSV format.');
        return;
      }
      setImportRows(rows);
    };
    reader.readAsText(file, 'UTF-8');
    // reset so same file can be re-uploaded
    e.target.value = '';
  };

  const handleImport = async () => {
    if (!importRows.length) return;
    setImporting(true);
    try {
      const res = await apiFetch(`${API_URL}/products/import`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ products: importRows }),
      });
      const data = await res.json();
      if (!res.ok) { message.error(data.error || 'Import failed'); return; }
      setImportResult(data);
      setImportRows([]);
      setImportFileName('');
      fetchProducts(); // refresh product list
    } catch {
      message.error('Import failed — network error');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const headers = 'Name,SKU,Price,Cost,Stock,Category,Description,Unit,Barcode,Supplier,Weight,Min Stock,Wholesale Price,Box Quantity,Min Order Qty,Tax Rate,Notes';
    const example = 'Example Product,SKU-001,9.99,5.00,100,Electronics,A great product,pcs,1234567890,,200g,10,7.99,12,6,20,';
    const csv = 'data:text/csv;charset=utf-8,' + headers + '\n' + example;
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', 'product-import-template.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const archiveProduct = async (id: string, name: string) => {
    if (!window.confirm(`Are you sure you want to archive "${name}"? You can restore it later.`)) return;

    try {
      if (!token) {
        navigate('/login');
        return;
      }

      const res = await apiFetch(`${API_URL}/products/${id}/archive`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        showToast('✅ Product archived successfully!');
        fetchProducts();
      } else {
        const error = await res.json();
        showToast(`❌ Failed to archive: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Archive error:', error);
      showToast('❌ Network error - check backend connection');
    }
  };

  const restoreProduct = async (id: string, name: string) => {
    if (!window.confirm(`Restore "${name}"?`)) return;

    try {
      if (!token) {
        navigate('/login');
        return;
      }

      const res = await apiFetch(`${API_URL}/products/${id}/restore`, {
        method: 'PATCH',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        showToast('✅ Product restored successfully!');
        fetchProducts();
      } else {
        const error = await res.json();
        showToast(`❌ Failed to restore: ${error.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Restore error:', error);
      showToast('❌ Network error - check backend connection');
    }
  };

  if (loading) {
    return <p style={{ padding: 40, textAlign: 'center' }}>⏳ Loading products...</p>;
  }

  if (error) {
    return (
      <PageLayout title="📦 Products" action={null}>
        <div style={{ textAlign: 'center', padding: 60, background: '#fff', borderRadius: 12 }}>
          <h2>❌ Error Loading Products</h2>
          <p>{error}</p>
          <Button type="primary" onClick={() => { setLoading(true); fetchProducts(); }}>Retry</Button>
        </div>
      </PageLayout>
    );
  }

  // Get unique categories from products
  const categories = ['All', ...Array.from(new Set(products.map(p => p.category || 'Uncategorized')))];

  // Apply filters
  const filteredProducts = products.filter(prod => {
    // Search filter (name, SKU, description)
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      prod.name?.toLowerCase().includes(searchLower) ||
      prod.sku?.toLowerCase().includes(searchLower) ||
      prod.description?.toLowerCase().includes(searchLower);

    // Category filter
    const matchesCategory = categoryFilter === 'All' || 
      (prod.category || 'Uncategorized') === categoryFilter;

    // Status filter
    const isArchived = Boolean(prod.deleted_at);
    const currentStatus = prod.status || 'Active';
    const matchesStatus =
      statusFilter === 'All' ||
      (statusFilter === 'Archived' && isArchived) ||
      (statusFilter === 'Active' && !isArchived && currentStatus === 'Active') ||
      (statusFilter === 'Inactive' && !isArchived && currentStatus === 'Inactive');

    // Stock filter
    const quantity = Number(prod.quantity ?? prod.stock ?? 0);
    const minStock = Number(prod.minStock ?? prod.min_stock ?? 10);
    const isLowStock = quantity <= minStock;
    const matchesStock = 
      stockFilter === 'All' ||
      (stockFilter === 'Low Stock' && isLowStock) ||
      (stockFilter === 'In Stock' && !isLowStock && quantity > 0) ||
      (stockFilter === 'Out of Stock' && quantity === 0);

    return matchesSearch && matchesCategory && matchesStatus && matchesStock;
  });

  const { pages: productPages, safePage: safeListPage, slice: paginatedProducts } =
    paginateList(filteredProducts, listPage);

  const renderProductActions = (prod: any, prodId: string | number) => {
    const id = String(prodId);
    return (
    <div className="prod-row-actions">
      {prod.deleted_at ? (
        <button type="button" className="prod-act prod-act--restore" onClick={() => restoreProduct(id, prod.name)}>Restore</button>
      ) : (
        <>
          <button type="button" className="prod-act prod-act--view" onClick={() => navigate(`/products/view/${id}`)}>View</button>
          <button type="button" className="prod-act prod-act--edit" onClick={() => navigate(`/products/edit/${id}`)}>Edit</button>
          <button type="button" className="prod-act prod-act--archive" onClick={() => archiveProduct(id, prod.name)}>Archive</button>
        </>
      )}
    </div>
    );
  };

  return (
    <PageLayout
      title="📦 Products"
      action={<Button type="primary" onClick={() => navigate('/products/add')}>Add Product</Button>}
    >

      <Card bordered={false} className="prod-list-page" style={{ border: 'none', boxShadow: 'none' }}>
      {/* Summary strip — light colours: blue, green, red, pink, white, cost-blue */}
      <div className="prod-stats">
        {[
          { label: 'Total Products', value: products.length, tone: 'blue' },
          { label: 'Active', value: products.filter(p => !p.deleted_at && (p.status || 'Active') === 'Active').length, tone: 'green' },
          { label: 'Low Stock', value: products.filter(p => !p.deleted_at && Number(p.quantity ?? p.stock ?? 0) <= Number(p.minStock ?? p.min_stock ?? 10)).length, tone: 'red' },
          { label: 'Expiring', value: products.filter(p => !p.deleted_at && isExpiringSoon(p.expiry_date || p.expiryDate)).length, tone: 'pink' },
          { label: 'Archived', value: products.filter(p => Boolean(p.deleted_at)).length, tone: 'white' },
          { label: 'Inventory Cost', value: `£${products.filter(p => !p.deleted_at).reduce((sum, p) => sum + (Number(p.cost || 0) * Number(p.quantity ?? p.stock ?? 0)), 0).toLocaleString('en-GB', { maximumFractionDigits: 0 })}`, tone: 'cost' },
        ].map((m, i) => (
          <div key={i} className={`prod-kpi prod-kpi--${m.tone}`}>
            <div className="prod-kpi-label">{m.label}</div>
            <div className="prod-kpi-value">{m.value}</div>
          </div>
        ))}
      </div>

      {/* Search and Filters */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
        <Space wrap>
          <Input
            placeholder="Search by name, SKU, or description..."
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ width: 300 }}
          />
          <Select
            value={categoryFilter}
            onChange={setCategoryFilter}
            style={{ width: 180 }}
          >
            {categories.map(cat => (
              <Select.Option key={cat} value={cat}>
                {cat === 'All' ? 'All Categories' : cat}
              </Select.Option>
            ))}
          </Select>

          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
          >
            <Select.Option value="All">All Products</Select.Option>
            <Select.Option value="Active">Active</Select.Option>
            <Select.Option value="Inactive">Inactive</Select.Option>
            <Select.Option value="Archived">Archived</Select.Option>
          </Select>

          <Select
            value={stockFilter}
            onChange={setStockFilter}
            style={{ width: 150 }}
          >
            <Select.Option value="All">All Stock</Select.Option>
            <Select.Option value="In Stock">In Stock</Select.Option>
            <Select.Option value="Low Stock">Low Stock</Select.Option>
            <Select.Option value="Out of Stock">Out of Stock</Select.Option>
          </Select>

        </Space>

        <Space>
          {(searchTerm || categoryFilter !== 'All' || statusFilter !== 'All' || stockFilter !== 'All') && (
            <Button
              onClick={() => {
                setSearchTerm('');
                setCategoryFilter('All');
                setStatusFilter('All');
                setStockFilter('All');
              }}
            >
              Clear Filters
            </Button>
          )}
          <Button icon={<UploadOutlined />} onClick={() => { setImportOpen(true); setImportResult(null); setImportRows([]); setImportFileName(''); }}>
            Import CSV
          </Button>
          <Button
            icon={<SearchOutlined />}
            onClick={() => {
              if (!products || products.length === 0) { message.warning('No products to export'); return; }
              const rows = filteredProducts.map(p => ({
                Name: p.name || '',
                SKU: p.sku || '',
                Category: p.category || 'Uncategorized',
                Price: Number(p.price || 0).toFixed(2),
                Cost: Number(p.cost || 0).toFixed(2),
                Stock: Number(p.quantity ?? p.stock ?? 0),
                Unit: p.unit || 'pcs',
                Status: p.status || 'Active',
                Description: (p.description || '').replace(/,/g, ' ')
              }));
              const csv = 'data:text/csv;charset=utf-8,' +
                Object.keys(rows[0]).join(',') + '\n' +
                rows.map(r => Object.values(r).join(',')).join('\n');
              const link = document.createElement('a');
              link.setAttribute('href', encodeURI(csv));
              link.setAttribute('download', 'products.csv');
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Export CSV
          </Button>
        </Space>
      </div>

      {/* Results count + pagination row */}
      <ListPagination
        total={products.length}
        filtered={filteredProducts.length}
        page={safeListPage}
        pages={productPages}
        onPageChange={setListPage}
        noun="products"
      />

      <div className="prod-table-panel">
      <div className="prod-table-wrap">
        <table className="prod-table" style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'collapse', minWidth: 920 }}>
          <thead>
            <tr>
              <th style={{ width: '28%' }}>Product</th>
              <th style={{ width: '10%' }}>Category</th>
              <th style={{ width: '11%' }}>Price</th>
              <th style={{ width: '8%' }}>Stock</th>
              <th style={{ width: '12%' }}>Expiry</th>
              <th style={{ width: '8%' }}>Status</th>
              <th className="prod-col-actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.length > 0 ? paginatedProducts.map(prod => {
              const prodId = prod._id || prod.id;
              const quantity = Number(prod.quantity ?? prod.stock ?? 0);
              const minStock = Number(prod.minStock ?? prod.min_stock ?? 10);
              const isLowStock = quantity <= minStock;
              const price = Number(prod.price || 0);
              const cost = Number(prod.cost || 0);
              const marginPct = price > 0 ? ((price - cost) / price) * 100 : 0;
              const marginColor = marginPct >= 35 ? '#059669' : marginPct >= 20 ? '#d97706' : '#dc2626';
              const expiry = getExpiryStatus(prod.expiry_date || prod.expiryDate);
              const expiryColors = {
                none: { bg: '#f9fafb', color: '#9ca3af' },
                ok: { bg: '#f0fdf4', color: '#166534' },
                soon: { bg: '#fffbeb', color: '#b45309' },
                expired: { bg: '#fef2f2', color: '#dc2626' },
              }[expiry.level];

              return (
                <tr key={prodId} style={s.row}>
                  <td style={s.td}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', minWidth: 0 }}>
                      {prod.image1 || prod.image ? (
                        <img
                          src={(prod.image1 || prod.image).startsWith('http') ? (prod.image1 || prod.image) : `${API_URL.replace('/api', '')}${prod.image1 || prod.image}`}
                          alt={prod.name}
                          style={s.productThumbnail}
                        />
                      ) : (
                        <div style={s.productThumbnailPlaceholder}>📦</div>
                      )}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <strong style={{ display: 'block', fontSize: 13 }}>{prod.name}</strong>
                        {prod.sku && <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>{prod.sku}</div>}
                      </div>
                    </div>
                  </td>
                  <td style={s.tdCompact}>{prod.category || '—'}</td>
                  <td style={s.tdCompact}>
                    <div style={{ fontWeight: 700 }}>£{Number(prod.price || 0).toFixed(2)}</div>
                    <span style={{
                      display: 'inline-block',
                      marginTop: 4,
                      padding: '2px 8px',
                      borderRadius: 10,
                      fontSize: 10,
                      fontWeight: 700,
                      background: `${marginColor}15`,
                      color: marginColor,
                    }}>
                      {Number.isFinite(marginPct) ? `${marginPct.toFixed(0)}% margin` : '—'}
                    </span>
                  </td>
                  <td style={s.tdCompact}>
                    <span style={{
                      ...s.stockBadge,
                      background: isLowStock ? '#FF4136' : '#2ECC40'
                    }}>
                      {quantity}
                    </span>
                    {isLowStock && (
                      <div style={{ ...s.lowStockWarning, marginTop: 4 }}>Low</div>
                    )}
                  </td>
                  <td style={s.tdCompact}>
                    {expiry.level === 'none' ? (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 700,
                        background: expiryColors.bg,
                        color: expiryColors.color,
                      }}>
                        {expiry.level === 'expired' ? '⛔' : expiry.level === 'soon' ? '⚠️' : ''} {expiry.label}
                      </span>
                    )}
                  </td>
                  <td style={s.tdCompact}>
                    <span style={{
                      ...s.statusBadge,
                      background: 
                        prod.deleted_at ? '#6b7280' :
                        prod.status === 'Active' ? '#2ECC40' :
                        prod.status === 'Inactive' ? '#AAAAAA' :
                        prod.status === 'Out of Stock' ? '#FF4136' :
                        '#bc00caff'
                    }}>
                      {prod.deleted_at ? 'Archived' : (prod.status || 'Active')}
                    </span>
                  </td>
                  <td className="prod-col-actions">
                    {renderProductActions(prod, prodId)}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={7} style={{...s.td, textAlign: 'center', padding: 40, color: '#999'}}>
                  {products.length === 0 
                    ? '📭 No products found. Add your first product!'
                    : '🔍 No products match your filters. Try adjusting your search.'
                  }
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      </div>

      <div className="prod-mobile-cards">
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>Loading...</div>
        ) : filteredProducts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>
            {products.length === 0
              ? '📭 No products found. Add your first product!'
              : '🔍 No products match your filters. Try adjusting your search.'}
          </div>
        ) : (
          paginatedProducts.map(prod => {
            const prodId = prod._id || prod.id;
            const quantity = Number(prod.quantity ?? prod.stock ?? 0);
            const minStock = Number(prod.minStock ?? prod.min_stock ?? 10);
            const isLowStock = quantity <= minStock;
            const expiry = getExpiryStatus(prod.expiry_date || prod.expiryDate);
            const imageSrc = prod.image1 || prod.image;
            const resolvedImage = imageSrc
              ? (imageSrc.startsWith('http') ? imageSrc : `${API_URL.replace('/api', '')}${imageSrc}`)
              : null;

            return (
              <div key={prodId} className="prod-mobile-card">
                <div className="prod-mobile-card-header">
                  {resolvedImage ? (
                    <img src={resolvedImage} alt={prod.name} style={s.productThumbnail} />
                  ) : (
                    <div style={s.productThumbnailPlaceholder}>📦</div>
                  )}
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="prod-mobile-card-name">{prod.name}</div>
                    {prod.sku && <div className="prod-mobile-card-sku">{prod.sku}</div>}
                  </div>
                </div>

                <div className="prod-mobile-card-info">
                  <div>
                    <div className="prod-mobile-card-label">Category</div>
                    {prod.category || '—'}
                  </div>
                  <div>
                    <div className="prod-mobile-card-label">Price</div>
                    £{Number(prod.price || 0).toFixed(2)}
                  </div>
                  <div>
                    <div className="prod-mobile-card-label">Stock</div>
                    <span style={{ ...s.stockBadge, background: isLowStock ? '#FF4136' : '#2ECC40' }}>{quantity}</span>
                    {isLowStock && <span style={{ ...s.lowStockWarning, marginLeft: 6 }}>Low</span>}
                  </div>
                  <div>
                    <div className="prod-mobile-card-label">Expiry</div>
                    {expiry.level === 'none' ? '—' : expiry.label}
                  </div>
                  <div>
                    <div className="prod-mobile-card-label">Status</div>
                    {prod.deleted_at ? 'Archived' : (prod.status || 'Active')}
                  </div>
                </div>

                <div className="prod-mobile-actions">
                  {renderProductActions(prod, prodId)}
                </div>
              </div>
            );
          })
        )}
      </div>
      </Card>

      {/* ── Import CSV Modal ─────────────────────────────────────────────── */}
      <Modal
        open={importOpen}
        onCancel={() => { setImportOpen(false); setImportRows([]); setImportFileName(''); setImportResult(null); }}
        footer={null}
        title="📥 Import Products from CSV"
        width={780}
      >
        {/* Supported formats note */}
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#1e40af' }}>
          <b>Supported formats:</b> Shopify, Zoho Inventory, WooCommerce, and any CSV with columns like
          <code style={{ margin: '0 4px', background: '#dbeafe', padding: '1px 5px', borderRadius: 4 }}>Name, SKU, Price, Stock, Category, Description</code>
          — column names are matched automatically.
        </div>

        {/* Template download */}
        <div style={{ marginBottom: 16 }}>
          <Button size="small" onClick={downloadTemplate}>⬇ Download Template CSV</Button>
          <span style={{ marginLeft: 10, fontSize: 12, color: '#6b7280' }}>Start with our template to ensure correct columns.</span>
        </div>

        {/* File picker */}
        <input ref={fileInputRef} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={handleFileChange} />
        <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()} style={{ marginBottom: 12 }}>
          {importFileName ? `📄 ${importFileName}` : 'Choose CSV File'}
        </Button>

        {/* Preview */}
        {importRows.length > 0 && !importResult && (
          <>
            <div style={{ margin: '12px 0 6px', fontWeight: 600, fontSize: 13 }}>
              Preview — {importRows.length} row{importRows.length !== 1 ? 's' : ''} found
              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: 8 }}>(showing first 5)</span>
            </div>
            <div style={{ overflowX: 'auto', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ background: '#f8fafc' }}>
                    {['Name','SKU','Price','Stock','Category','Unit','Supplier'].map(h => (
                      <th key={h} style={{ padding: '6px 10px', textAlign: 'left', border: '1px solid #e2e8f0', fontWeight: 700, color: '#374151' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {importRows.slice(0, 5).map((r, i) => (
                    <tr key={i} style={{ background: i % 2 ? '#fafafa' : '#fff' }}>
                      {['name','sku','price','stock','category','unit','supplier'].map(f => (
                        <td key={f} style={{ padding: '5px 10px', border: '1px solid #e2e8f0', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r[f] || <span style={{ color: '#d1d5db' }}>—</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button type="primary" loading={importing} onClick={handleImport} style={{ marginRight: 8 }}>
              Import {importRows.length} Products
            </Button>
            <Button onClick={() => { setImportRows([]); setImportFileName(''); }}>Clear</Button>
          </>
        )}

        {/* Result */}
        {importResult && (
          <div style={{ marginTop: 12 }}>
            <div style={{ padding: '14px 18px', borderRadius: 8, background: importResult.failed === 0 ? '#f0fdf4' : '#fff7ed', border: `1.5px solid ${importResult.failed === 0 ? '#86efac' : '#fed7aa'}` }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 6, color: importResult.failed === 0 ? '#15803d' : '#c2410c' }}>
                {importResult.failed === 0 ? '✅' : '⚠️'} Import complete
              </div>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#15803d', fontWeight: 600 }}>✓ {importResult.inserted} imported successfully</span>
                {importResult.failed > 0 && <span style={{ color: '#dc2626', fontWeight: 600, marginLeft: 16 }}>✗ {importResult.failed} failed</span>}
              </div>
              {importResult.errors.length > 0 && (
                <details style={{ marginTop: 10 }}>
                  <summary style={{ cursor: 'pointer', fontSize: 12, color: '#6b7280' }}>Show errors</summary>
                  <ul style={{ marginTop: 6, fontSize: 12, paddingLeft: 18 }}>
                    {importResult.errors.map((e: any, i: number) => (
                      <li key={i}>Row {e.row}: {e.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <Button style={{ marginTop: 12 }} onClick={() => setImportOpen(false)}>Close</Button>
          </div>
        )}
      </Modal>

    </PageLayout>
  );
};

const s: any = {
  container: {
    maxWidth: 1400,
    margin: '40px auto',
    fontFamily: 'Arial, sans-serif',
    padding: '0 20px'
  },
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
    alignItems: 'center',
    background: '#fff',
    padding: 20,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  searchInput: {
    flex: '1 1 300px',
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s',
    fontFamily: 'Arial, sans-serif'
  },
  filterSelect: {
    padding: '12px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: 8,
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    background: '#fff',
    transition: 'border-color 0.2s',
    fontFamily: 'Arial, sans-serif',
    minWidth: 150
  },
  clearButton: {
    padding: '12px 20px',
    background: '#FF4136',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'background 0.2s'
  },
  resultsInfo: {
    fontSize: 14,
    color: '#6c757d',
    marginBottom: 15,
    fontWeight: 500
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 25
  },
  title: {
    margin: 0,
    color: '#333'
  },
  addBtn: {
    padding: '12px 24px',
    background: '#2ECC40',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontWeight: 600,
    fontSize: 15
  },
  tableWrapper: {
    background: '#fff',
    borderRadius: 2,
    border: '1px solid #f0f0f0',
    overflow: 'hidden',
    marginTop: 16
  },
  table: {
    width: '100%',
    tableLayout: 'fixed' as const,
    borderCollapse: 'collapse'
  },
  headerRow: {
    background: '#fafafa',
    borderBottom: '1px solid #f0f0f0'
  },
  th: {
    padding: '10px 8px',
    textAlign: 'left' as const,
    fontWeight: 600,
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.85)',
  },
  row: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.2s'
  },
  td: {
    padding: '10px 8px',
    fontSize: 13,
    color: 'rgba(0, 0, 0, 0.85)',
    verticalAlign: 'middle' as const,
  },
  tdCompact: {
    padding: '10px 8px',
    fontSize: 12,
    color: 'rgba(0, 0, 0, 0.85)',
    verticalAlign: 'middle' as const,
    wordBreak: 'break-word' as const,
  },
  productThumbnail: {
    width: 40,
    height: 40,
    objectFit: 'cover' as const,
    borderRadius: 4,
    border: '1px solid #eee',
    flexShrink: 0,
  },
  productThumbnailPlaceholder: {
    width: 40,
    height: 40,
    background: '#f5f5f5',
    borderRadius: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    color: '#999',
    border: '1px dashed #ddd'
  },
  description: {
    fontSize: 12,
    color: '#6c757d',
    marginTop: 4,
    maxWidth: 300,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  costText: {
    fontSize: 12,
    color: '#6c757d',
    marginTop: 2
  },
  stockBadge: {
    padding: '4px 10px',
    borderRadius: 12,
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block'
  },
  lowStockWarning: {
    fontSize: 11,
    color: '#FF4136',
    marginTop: 4,
    fontWeight: 600
  },
  statusBadge: {
    padding: '5px 12px',
    borderRadius: 12,
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    display: 'inline-block'
  },
  btnView: {
    padding: '6px 12px',
    backgroundColor: '#2ECC40',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: 6,
    fontSize: 13,
    fontWeight: 500
  },
  btnEdit: {
    padding: '6px 12px',
    backgroundColor: '#0074D9',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: 6,
    fontSize: 13,
    fontWeight: 500
  },
  btnDelete: {
    padding: '6px 12px',
    backgroundColor: '#FF4136',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
    marginTop: 30
  },
  statCard: {
    flex: 1,
    background: '#fff',
    padding: 20,
    borderRadius: 10,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    textAlign: 'center'
  },
  statLabel: {
    fontSize: 13,
    color: '#6c757d',
    marginBottom: 8,
    fontWeight: 600
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#333'
  },
  archivedBadge: {
    padding: '6px 12px',
    background: '#FF4136',
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    borderRadius: 6,
    display: 'inline-block'
  }
};

export default ProductList;